// src/Card.ts
import { v4 as uuidv4 } from "uuid";
import { getParents, getVariableContent, stopEvent } from "@sandstein/bitv-common";
import { scrollIntoViewIfNeeded } from "@sandstein/bitv-motion";
var Card = class {
  /**
   * Card constructor.
   */
  constructor(el, options = {}, eventBus) {
    /**
     * Holds a reference to the image caption if present.
     */
    this.caption = null;
    /**
     * Holds a reference to all descriptor elements
     * that will be added via aria-describedby to the card title.
     */
    this.descriptors = [];
    /**
     * Holds a reference to the focus helper element.
     */
    this.focusHelper = null;
    /**
     * Holds a reference to inner card element.
     */
    this.inner = null;
    /**
     * Tells if the full card should be clickable/interactive (only applied if
     * a main action is found within the card).
     */
    this.isInteractive = false;
    /**
     * Holds a reference to the main action of the card, if any.
     * The main action can be a link, a button, or any other element with role="button".
     */
    this.mainAction = null;
    /**
     * Tells if a mouse click was a middle click.
     */
    this.middleClick = false;
    /**
     * Holds a reference to all links that point to the same href as the main action.
     */
    this.similarActions = [];
    /**
     * Holds a reference to the card title.
     * The card title is the element where all aria-describedby relations will be added to.
     * It is either the headline itself, or the link element within a headline.
     */
    this.title = null;
    /**
     * Event handler that handles click events on the card container.
     * If the click target was not another interactive element,
     * then the click event will be passed through to the card title/link.
     */
    this.onClick = (event) => {
      if (this.inner) {
        const isHeroActive = getVariableContent(this.options.cardHeroActiveVar, this.inner) === "1";
        const clickedMediaElement = getParents(event.target).filter((parentElement) => parentElement.classList.contains(this.options.mediaClass)).length > 0;
        if (isHeroActive && clickedMediaElement) {
          return;
        }
      }
      const clickedInteractiveElement = getParents(event.target).filter((parentElement) => parentElement.classList.contains(this.options.captionClass) || parentElement.tagName === "A" || parentElement.tagName === "BUTTON" || parentElement.getAttribute("role") === "button").length > 0;
      if (clickedInteractiveElement) {
        return;
      }
      if (event.isTrusted) {
        stopEvent(event);
      }
      const controlKeyPressed = event.ctrlKey;
      if (controlKeyPressed) {
        this.mainAction?.dispatchEvent(new MouseEvent("click", { ctrlKey: controlKeyPressed, bubbles: false }));
      } else {
        this.mainAction?.click();
      }
    };
    /**
     * Event handler to catch mouse down events of a middle mouse button click
     * to react open the link in a new tab.
     */
    this.onMousedown = (event) => {
      if (!this.isInteractive) {
        return;
      }
      this.middleClick = event.button === 1;
      if (this.middleClick) {
        stopEvent(event);
      }
    };
    /**
     * Event handler to catch mouse up events of a middle mouse button click.
     */
    this.onMouseup = (event) => {
      if (!this.isInteractive) {
        return;
      }
      if (!this.middleClick) {
        return;
      }
      stopEvent(event);
      this.middleClick = false;
      this.mainAction?.dispatchEvent(new MouseEvent("click", { ctrlKey: true, bubbles: false }));
    };
    this.onMainActionFocus = () => {
      if (this.focusHelper && document.documentElement.getAttribute("data-wi") === "keyboard") {
        scrollIntoViewIfNeeded(this.focusHelper, { behavior: "auto", block: "nearest", inline: "nearest" });
      }
    };
    this.el = el;
    this.eventBus = eventBus;
    this.abortController = new AbortController();
    const defaults = {
      captionClass: "caption",
      cardHeroActiveVar: "--_card-hero-active",
      descriptorClass: "card--descriptor",
      focusHelperClass: "card--focus-helper",
      headerClass: "card--header",
      innerClass: "card--inner",
      interactiveClass: "card__interactive",
      mainActionClass: "card--main-action",
      mediaClass: "card--media"
    };
    this.options = {
      ...defaults,
      ...options
    };
    this.initialize();
  }
  /**
   * Initialize the card.
   */
  initialize() {
    const header = this.el.querySelector(`.${this.options.headerClass}`);
    const headline = header?.querySelector("h1, h2, h3, h4, h5, h6");
    if (headline) {
      const headlineLink = headline.querySelector("a[href]");
      this.title = headlineLink ? headlineLink : headline;
    }
    this.determineMainAction();
    this.isInteractive = this.el.classList.contains(this.options.interactiveClass);
    if (this.isInteractive && !this.mainAction) {
      this.isInteractive = false;
      this.el.classList.remove(this.options.interactiveClass);
      console.warn("[Card] is declared interactive but has no main action. The interactive class has been removed.", this.el);
    }
    if (this.isInteractive && this.mainAction?.tagName === "A") {
      const linksAndButtons = Array.from(this.el.querySelectorAll(`a[href], button, [role="button"]`));
      this.similarActions = linksAndButtons.filter((el) => {
        if (el.tagName !== "A" || el === this.mainAction) {
          return;
        }
        return el.getAttribute("href") === this.mainAction.getAttribute("href");
      });
    }
    this.descriptors = Array.from(this.el.querySelectorAll(`.${this.options.descriptorClass}`));
    this.inner = this.el.querySelector(`.${this.options.innerClass}`);
    this.caption = this.el.querySelector(`.${this.options.captionClass}`);
    if (this.isInteractive) {
      this.focusHelper = document.createElement("div");
      this.focusHelper.classList.add(this.options.focusHelperClass);
      this.focusHelper.setAttribute("aria-hidden", "true");
      this.inner?.prepend(this.focusHelper);
    }
    this.setupAria();
    this.attachEvents();
  }
  /**
   * Determine the main action of the card.
   */
  determineMainAction() {
    const mainActions = Array.from(this.el.querySelectorAll(`.${this.options.mainActionClass}`));
    if (mainActions.length > 1) {
      console.warn(`[Card] has ${mainActions.length} main actions. Allowed is only 1 main action.`, this.el, mainActions);
      return;
    }
    if (mainActions.length > 0) {
      this.mainAction = mainActions[0];
      return;
    }
    if (this.title?.tagName === "A") {
      this.mainAction = this.title;
    }
  }
  /**
   * Setup all aria states of the card and its subcomponents.
   */
  setupAria() {
    const describedByIds = [];
    this.descriptors.forEach((descriptor) => {
      let id = descriptor.getAttribute("id");
      if (!id) {
        id = uuidv4();
        descriptor.setAttribute("id", id);
      }
      describedByIds.push(id);
    });
    if (this.title) {
      const titleAriaDescribedBy = this.title.getAttribute("aria-describedby");
      const describedBy = [
        titleAriaDescribedBy,
        ...describedByIds
      ].filter((x) => x !== null);
      if (describedBy.length > 0) {
        this.title.setAttribute("aria-describedby", describedBy.join(" "));
      }
    }
    this.similarActions.forEach((similarAction) => {
      similarAction.setAttribute("tabindex", "-1");
    });
  }
  /**
   * Attach event handlers to the card container.
   */
  attachEvents() {
    if (!this.isInteractive) {
      return;
    }
    this.el.addEventListener("click", this.onClick, {
      signal: this.abortController.signal
    });
    this.el.addEventListener("mousedown", this.onMousedown, {
      signal: this.abortController.signal
    });
    this.el.addEventListener("mouseup", this.onMouseup, {
      signal: this.abortController.signal
    });
    this.mainAction?.addEventListener("focus", this.onMainActionFocus, {
      signal: this.abortController.signal
    });
  }
  destroy() {
    this.abortController.abort();
    this.focusHelper?.remove();
  }
};
export {
  Card
};
